import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ATMPageLoader from "src/components/UI/atoms/ATMPageLoader/ATMPageLoader";
import {
  useGetSinglePanQuery,
  useGetPanFormByPanIdQuery,
} from "src/services/PANService";
import PanFormDialog from "src/pages/pan/view/Dialog/PanFormDialogBox/PanFormDialog";

/**
 * The PAN form on a page — and a window — of its own.
 *
 * It used to be a dialog on the application detail page, which meant filling a
 * two-page government form inside a scrolling box. The form itself is unchanged —
 * only the frame around it — so `PanFormDialog` is mounted here as-is.
 *
 * The frame is deliberately BARE: no side navigation, no header search. FILL PAN
 * FORM opens this url as its own full-screen window, and everything in that
 * window belongs to the one application being filled. A nav rail and a global
 * search bar there are only two ways to lose the half-filled form, and they eat
 * the width the two-page form wants. Leaving is the form's own Close button.
 *
 * Submitting it generates the PDF and, on the API side, moves the application to
 * GENERATE and routes it to a print operator. There is nothing else to click
 * afterwards, so a successful save returns to the form-fill queue.
 */

const PanFormPageWrapper = () => {
  const { panApplicationId } = useParams();
  const navigate = useNavigate();
  /*
    `?mode=refill` — opened by REFILL PAN FORM: the form was already generated
    and is reopened with its saved values to be corrected and generated again.
  */
  const [searchParams] = useSearchParams();
  const isRefillRequest = searchParams.get("mode") === "refill";
  /*
    `&type=NEW|CORRECTION` — the form type picked in the Refill dialog. Only
    the PDF is generated in that type; the application keeps its own.
  */
  const typeParam = searchParams.get("type");
  const refillType: "NEW" | "CORRECTION" | null =
    isRefillRequest && (typeParam === "NEW" || typeParam === "CORRECTION")
      ? typeParam
      : null;

  /**
   * PanFormDialog fires onGenerated and then onClose on a successful save. Both
   * would navigate, and onClose would win — landing the operator back on the
   * application they just finished instead of the next one in the queue.
   */
  const generatedRef = React.useRef(false);

  const { data, isFetching, isLoading } = useGetSinglePanQuery(panApplicationId);
  const applicationData = data?.data || null;
  /*
    The form already saved for this application, read for one thing: the remark
    the last generate stored inside its `formData`.

    The remark's proper home is the PAN application itself, but an API build
    that predates that field drops it — and then re-opening this page would show
    an empty Remark box and the next generate would overwrite the note with
    nothing. Seeding from the saved form as well means the note survives a
    re-generate on either build.
  */
  const {
    data: savedPanForm,
    isLoading: isSavedFormLoading,
    isFetching: isSavedFormFetching,
  } = useGetPanFormByPanIdQuery(panApplicationId || "", {
    skip: !panApplicationId,
  });
  /*
    Refill needs a form to refill. Without a saved one (or with an empty
    formData) the page falls back to an ordinary first fill.
  */
  const savedFormData = savedPanForm?.data?.formData;
  const isRefill =
    isRefillRequest &&
    !!savedFormData &&
    typeof savedFormData === "object" &&
    Object.keys(savedFormData).length > 0;
  const savedRemark: string =
    applicationData?.formFillRemark ||
    savedPanForm?.data?.formData?.formFillRemark ||
    "";

  // Refill must wait for the saved form too: PanFormDialog reads it once, when
  // it initialises.
  if (
    isFetching ||
    isLoading ||
    (isRefillRequest && (isSavedFormLoading || isSavedFormFetching))
  ) {
    return <ATMPageLoader />;
  }

  /**
   * The form is only editable while the application is in the form-fill stage.
   * Reaching this url any other way — a stale tab, a bookmarked link, the back
   * button after submitting — must not offer an editable form for an application
   * that has already been printed or rejected.
   */
  /*
    REFILL widens that to every stage REFILL PAN FORM is offered on (see the
    button in VIewPanCardApplication): LOGIN DONE, GENERATE and DONE.
    Generating again there replaces the PDF (the old one is kept in Old PAN
    Forms) and leaves the status where it is — the API only moves it out of
    FORM_FILL_IN_PROGRESS. DONE used to be left out here while the button
    offered it, so REFILL on a DONE application opened this "can no longer be
    edited" page.
  */
  const editableStatuses = isRefillRequest
    ? ["FORM_FILL_IN_PROGRESS", "VERIFY", "GENERATE", "DONE"]
    : ["FORM_FILL_IN_PROGRESS"];
  if (applicationData && !editableStatuses.includes(applicationData.status)) {
    return (
      <div className="h-screen w-screen overflow-auto bg-white flex flex-col gap-4 p-6">
        <div className="text-lg text-primary-dark font-medium">PAN Form</div>
        <div className="text-slate-600 text-sm">
          This application is in{" "}
          <span className="font-medium">
            {(applicationData.status || "").replace(/_/g, " ")}
          </span>
          , so its PAN form can no longer be edited.
        </div>
        <div>
          <button
            onClick={() => {
              /* Opened as its own window — closing it is the way back. */
              if (window.opener) {
                window.close();
                return;
              }
              navigate(`/pan/${panApplicationId}`);
            }}
            className="px-4 py-2 rounded bg-primary-main text-white text-[12px] font-medium"
          >
            BACK TO APPLICATION
          </button>
        </div>
      </div>
    );
  }

  return (
    /* The window IS the form: it owns the full viewport and scrolls as one. */
    <div className="h-screen w-screen overflow-auto bg-white flex flex-col gap-3">
      {/* The only application detail carried above the form — who it is for,
          and which application it is — so the operator can tell at a glance
          which of several open form windows they are looking at. */}
      <div className="flex px-6 pt-4 items-baseline gap-3 flex-wrap">
        <div className="text-lg text-primary-dark font-medium">
          {applicationData?.name}
        </div>
        <div className="text-sm text-slate-600">
          {applicationData?.srn || "-"}
        </div>
        <div className="bg-primary-light px-3 py-1 rounded-full text-primary-main text-xs">
          {(refillType || applicationData?.applicationType) === "CORRECTION"
            ? "Correction"
            : "New (Form 49A)"}
        </div>
        {isRefill && (
          <div className="bg-amber-100 px-3 py-1 rounded-full text-amber-800 text-xs font-medium">
            Refill — saved details loaded; generating makes a new PDF (the old one is kept)
          </div>
        )}
        {isRefillRequest && !isRefill && (
          <div className="bg-red-50 px-3 py-1 rounded-full text-red-700 text-xs font-medium">
            No saved form found — filling fresh from the application
          </div>
        )}
        {applicationData?.acknowledgementNumber && (
          <div className="bg-primary-light px-3 py-1 rounded-full text-primary-main text-xs">
            Ack {applicationData.acknowledgementNumber}
          </div>
        )}
      </div>

      {/* The form's action bar is pinned to the top of this scroll box, so
          the foot of the form is the foot of the page and wants clearing. */}
      <div className="px-6 pb-6">
        <PanFormDialog
          applicationData={applicationData}
          savedRemark={savedRemark}
          savedFormData={isRefill ? savedFormData : null}
          applicationTypeOverride={refillType}
          savedApplicationType={
            savedPanForm?.data?.applicationType ||
            applicationData?.applicationType ||
            null
          }
          onClose={() => {
            if (generatedRef.current) return;
            /*
              FILL PAN FORM opens this page in its own window so the application
              detail page stays open to copy from. Cancelling should hand the
              operator back to that page — which is already sitting behind
              this window — rather than open a second copy of it here.
            */
            if (window.opener) {
              window.close();
              return;
            }
            navigate(`/pan/${panApplicationId}`);
          }}
          onGenerated={() => {
            // The API's own message says where the application was routed, and
            // PanFormDialog has already shown it.
            generatedRef.current = true;
            /*
              A refill does not move the application, so there is no queue to
              send the operator on to: the application page behind this window
              is reloaded instead, to show the new PDF.
            */
            if (isRefillRequest) {
              if (window.opener) {
                try {
                  if (!window.opener.closed) window.opener.location.reload();
                } catch {
                  // A tab we are not allowed to touch. Closing ours is still right.
                }
                window.setTimeout(() => window.close(), 800);
                return;
              }
              navigate(`/pan/${panApplicationId}`);
              return;
            }
            /*
              Submitted from the window FILL PAN FORM opened. The application
              page is still sitting behind it, now showing a stage this
              application has just left — so send that tab on to the form-fill
              queue and close this window, leaving the operator on the single
              tab they started from rather than on two copies of the same queue.

              The close is deferred a beat: the generated PDF has only just
              been handed to the browser as a download, and tearing the window
              down on the same tick can take the download with it.
            */
            if (window.opener) {
              try {
                if (!window.opener.closed) {
                  window.opener.location.href = "/pan/FORM_FILL_IN_PROGRESS";
                }
              } catch {
                // A tab we are not allowed to touch. Closing ours is still right.
              }
              window.setTimeout(() => window.close(), 800);
              return;
            }
            navigate("/pan/FORM_FILL_IN_PROGRESS");
          }}
        />
      </div>
    </div>
  );
};

export default PanFormPageWrapper;
